// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import {
  CircleNotch,
  DownloadSimple,
  File as FileIcon,
  FileDoc,
  FileImage,
  FilePdf,
  FilePpt,
  FileText,
  FileVideo,
  FileXls,
} from "@phosphor-icons/react";
import { Badge, Button, Tooltip } from "@radix-ui/themes";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2RailDocument } from "./types";

import { downloadAsset } from "../../utils/http";

const DOCUMENT_ICON_CLASS = "text-puntt-neutral-gray-11";

function documentTypeIcon(
  type: string,
  name: string,
): { Icon: typeof FileIcon; kind: string } {
  const ext = name.includes(".")
    ? name.split(".").pop()?.toLowerCase()
    : undefined;
  switch (ext) {
    case "pdf":
    case "ai":
      return { Icon: FilePdf, kind: "pdf" };
    case "doc":
    case "docx":
      return { Icon: FileDoc, kind: "doc" };
    case "xls":
    case "xlsx":
    case "csv":
      return { Icon: FileXls, kind: "sheet" };
    case "ppt":
    case "pptx":
      return { Icon: FilePpt, kind: "slides" };
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
      return { Icon: FileImage, kind: "image" };
    case "mp4":
    case "mov":
    case "avi":
    case "webm":
      return { Icon: FileVideo, kind: "video" };
    case "txt":
    case "md":
      return { Icon: FileText, kind: "text" };
    default:
      if (type === "image") return { Icon: FileImage, kind: "image" };
      if (type === "video") return { Icon: FileVideo, kind: "video" };
      return { Icon: FileIcon, kind: "file" };
  }
}

function RailDocumentName({ name, testId }: { name: string; testId: string }) {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <Tooltip content={name} side="left" open={open} delayDuration={200}>
      <span
        ref={nameRef}
        className="min-w-0 flex-1 truncate font-medium"
        data-testid={testId}
        onMouseEnter={() => {
          const el = nameRef.current;
          // why: a name that already fits has nothing to reveal — same gate as nav recents
          if (el) setOpen(el.scrollWidth > el.clientWidth);
        }}
        onMouseLeave={() => setOpen(false)}
      >
        {name}
      </span>
    </Tooltip>
  );
}

export function AskV2RailDocumentCard({
  document,
}: {
  document: AskV2RailDocument;
}) {
  const { t } = useTranslation("ui");
  const [downloading, setDownloading] = useState(false);
  const testId = `ask-v2-rail-document-${document.fileId}-v${document.versionOrdinal}`;
  const { Icon: TypeIcon, kind: typeKind } = documentTypeIcon(
    document.type,
    document.name,
  );

  const handleDownload = async () => {
    if (downloading || !document.source) return;
    setDownloading(true);
    try {
      await downloadAsset(document.source, document.name);
    } catch {
      // downloadAsset already handles user-visible failure paths.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      type="button"
      size="2"
      variant="surface"
      color="gray"
      highContrast
      onClick={() => {
        void handleDownload();
      }}
      disabled={downloading || !document.source}
      data-testid={testId}
      aria-label={
        document.showVersionBadge
          ? t("campaignDocsPanel.downloadFileVersion", {
              name: document.name,
              version: document.versionOrdinal,
            })
          : t("campaignDocsPanel.downloadFile", { name: document.name })
      }
      // why: radix centers a content-sized button; this row has to fill the rail and truncate
      className="group w-full !min-w-0 !shrink !justify-start !text-left"
    >
      <span
        aria-hidden="true"
        className="relative inline-flex size-4 shrink-0"
        data-testid={`${testId}-type`}
      >
        <TypeIcon
          size={16}
          className={`${DOCUMENT_ICON_CLASS} group-hover:!opacity-0 group-focus-visible:!opacity-0`}
          data-file-icon={typeKind}
        />
        {/* why: radix sets svg opacity, so the swap needs important to win */}
        <DownloadSimple
          size={16}
          className={`${DOCUMENT_ICON_CLASS} absolute inset-0 !opacity-0 group-hover:!opacity-100 group-focus-visible:!opacity-100`}
          data-testid={`${testId}-download`}
        />
      </span>
      <RailDocumentName name={document.name} testId={`${testId}-name`} />
      {document.showVersionBadge ? (
        <Badge
          size="1"
          variant="soft"
          color="gray"
          className="shrink-0"
          data-testid={`${testId}-version`}
        >
          V{document.versionOrdinal}
        </Badge>
      ) : null}
      {downloading ? (
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-neutral-gray-9"
          aria-hidden="true"
        />
      ) : null}
    </Button>
  );
}
